'use client';

import { Box, freddyTheme, Typography } from '@coolblue-development/freddy';
import { useStreamHandler } from '@coolblue-development/next-chat/client';
import type { PostMessageResponse, UiMessage } from '@coolblue-development/next-chat/types';
import { AdviceCue, type AdviceCueState } from '@coolblue-development/next-chatbot-ui/client';
import type { Product } from '@features/product/typings';
import fetchAdviceAgentSession from '@features/product-page/services/fetchAdviceAgentSession';
import useClientStorage from '@hooks/useClientStorage';
import { useIsHydrated } from '@hooks/useIsHydrated';
import { useTranslation } from '@i18n/useTranslation';
import { type ReactNode, useEffect, useRef, useState, useTransition } from 'react';

type Props = {
  accountName?: string;
  correlationId: string;
  endpointUrl: string;
  product: Product;
};

const APPLE_BRAND_ID = '32';
const PRODUCT_TYPE_IDS = {
  LAPTOP: '17632',
  TABLET: '2101',
  MOBILE_PHONE: '2093'
};

const AdviceCueWrapper = ({
  accountName,
  correlationId,
  endpointUrl,
  product
}: Props): ReactNode => {
  const { t } = useTranslation('webshop.productPage');

  const boxRef = useRef<HTMLDivElement>(null);
  const [, startTransition] = useTransition();

  const isHydrated = useIsHydrated();
  const [history, setHistory] = useState<UiMessage[]>([]);
  const [followUpQuestions, setFollowUpQuestions] = useClientStorage<string[] | []>(
    'adviceCueFollowUpQuestions_' + product.id,
    [],
    'localStorage'
  );

  const chatSource = 'product_advice_cue_agent';
  const { brand, productType, id } = product;
  const { productTypeId } = productType;

  let productTypeName = productType.singularName;

  if (productTypeId && String(brand?.id) === APPLE_BRAND_ID) {
    if (productTypeId === PRODUCT_TYPE_IDS.LAPTOP) {
      productTypeName = 'MacBook';
    } else if (productTypeId === PRODUCT_TYPE_IDS.TABLET) {
      productTypeName = 'iPad';
    } else if (productTypeId === PRODUCT_TYPE_IDS.MOBILE_PHONE) {
      productTypeName = 'iPhone';
    }
  }

  useEffect(() => {
    startTransition(() => {
      void (async () => {
        const { historyMessages } = await fetchAdviceAgentSession(chatSource, id.toString());
        setHistory(historyMessages);
      })();
    });
  }, [chatSource, id]);

  const { state, sendUserMessage } = useStreamHandler<AdviceCueState>({
    conversationEndpointUrl: endpointUrl,
    initialState: {
      uiMessages: [],
      showError: false,
      followUpQuestions: product.adviceCueAdvice?.questions ?? []
    },
    correlationId,
    customMessageHandlers: {
      AdviceCueFollowupQuestions: (
        incomingMessage: PostMessageResponse,
        currentState: AdviceCueState
      ) => {
        if (incomingMessage.type === 'AdviceCueFollowupQuestions') {
          if (incomingMessage?.payload?.questions?.length)
            setFollowUpQuestions(incomingMessage?.payload?.questions);
          return {
            ...currentState,
            followUpQuestions: incomingMessage?.payload?.questions || []
          };
        }
        return currentState;
      },
      agentProgress: (incomingMessage: PostMessageResponse, currentState: AdviceCueState) => {
        if (incomingMessage.type === 'agentProgress') {
          return {
            ...currentState,
            progressMessage: incomingMessage.payload?.message ?? undefined
          };
        }

        return currentState;
      }
    }
  });

  const mergedState: AdviceCueState = {
    ...state,
    uiMessages: [...history, ...state.uiMessages],
    followUpQuestions:
      isHydrated && followUpQuestions.length
        ? followUpQuestions
        : product.adviceCueAdvice?.questions || []
  };

  const adjustScrollPosition = () => {
    boxRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box
      ref={boxRef}
      sx={{
        position: 'sticky',
        top: `calc(var(--cb-header-height) + ${freddyTheme.spacing[1000]})`,
        scrollMarginTop: freddyTheme.spacing[1000],
        [freddyTheme.breakpoint.minWidth('md')]: {
          scrollMarginTop: `calc(var(--cb-header-height) + ${freddyTheme.spacing[1000]})`
        }
      }}
    >
      <AdviceCue
        title={
          accountName
            ? t('adviceCues.title.withName', {
                customerName: accountName,
                productType: productTypeName
              })
            : t('adviceCues.title.withoutName', {
                productType: productTypeName
              })
        }
        state={mergedState}
        onSendMessage={message => {
          sendUserMessage(message, {
            chatSource: chatSource
          });
          adjustScrollPosition();
        }}
        productTypeName={productTypeName}
      />
      <Typography variant='caption' mt={200}>
        This feature is currently available only for internal users.
      </Typography>
    </Box>
  );
};

export default AdviceCueWrapper;
